import { z } from "zod";

/**
 * Routines (ARCHITECTURE §6.9): a saved prompt plus a schedule, run by the
 * host whether or not the user is there. The prototype calls them automations
 * and gives them either a cron schedule or an event listener; ours are
 * schedule-only, so `schedule` is required and there is no trigger field.
 */

export const ROUTINE_MAX_PER_AGENT = 50;
export const ROUTINE_NAME_MAX_CHARS = 80;
export const ROUTINE_PROMPT_MAX_CHARS = 4_000;
export const ROUTINE_MAX_RUN_HISTORY = 20;
export const ROUTINE_RUN_DETAIL_MAX_CHARS = 300;

/** How a run started: the schedule came round, or the user pressed Run now. */
export const RoutineRunTriggerSchema = z.enum(["schedule", "manual"]);
export type RoutineRunTrigger = z.infer<typeof RoutineRunTriggerSchema>;

export const RoutineRunStatusSchema = z.enum(["running", "ok", "error"]);
export type RoutineRunStatus = z.infer<typeof RoutineRunStatusSchema>;

export const RoutineRunSchema = z.object({
  id: z.string().min(1),
  trigger: RoutineRunTriggerSchema,
  startedAt: z.number().int().nonnegative(),
  finishedAt: z.number().int().nonnegative().nullable(),
  status: RoutineRunStatusSchema,
  /** One line about how it went; for an error, what failed. */
  detail: z.string().optional(),
}).strict();
export type RoutineRun = z.infer<typeof RoutineRunSchema>;

export const RoutineSchema = z.object({
  id: z.string().min(1),
  agentId: z.string().min(1),
  name: z.string().min(1),
  /** What to do each time, written to the agent's future self. */
  prompt: z.string().min(1),
  /** Five cron fields, an `@daily`-style shorthand, or `@every 30m`; an optional `CRON_TZ=` prefix pins the zone. */
  schedule: z.string().min(1),
  /** The schedule in words, for the list; derived, never stored. */
  scheduleDescription: z.string(),
  enabled: z.boolean(),
  createdAt: z.number().int().nonnegative(),
  lastRunAt: z.number().int().nonnegative().nullable(),
  /** When it next comes round; null when it is paused or the schedule never matches again. */
  nextRunAt: z.number().int().nonnegative().nullable(),
  runs: z.array(RoutineRunSchema),
}).strict();
export type Routine = z.infer<typeof RoutineSchema>;

export const CreateRoutineRequestSchema = z.object({
  name: z.string().min(1),
  prompt: z.string().min(1),
  schedule: z.string().min(1),
  enabled: z.boolean().optional(),
}).strict();
export type CreateRoutineRequest = z.infer<typeof CreateRoutineRequestSchema>;

/** PATCH /agents/:id/routines/:routineId. Every field is optional; those given replace what is there. */
export const UpdateRoutineRequestSchema = z.object({
  name: z.string().min(1).optional(),
  prompt: z.string().min(1).optional(),
  schedule: z.string().min(1).optional(),
  enabled: z.boolean().optional(),
}).strict();
export type UpdateRoutineRequest = z.infer<typeof UpdateRoutineRequestSchema>;

/** GET /agents/:id/routines: the agent's routines, soonest first, and the zone their schedules are read in. */
export const RoutineListResponseSchema = z.object({
  routines: z.array(RoutineSchema),
  timeZone: z.string().min(1),
}).strict();
export type RoutineListResponse = z.infer<typeof RoutineListResponseSchema>;

/** POST /agents/:id/routines/:routineId/run: the turn the routine opened. */
export const RoutineRunResponseSchema = z.object({ runId: z.string().min(1), turnId: z.string().min(1) }).strict();
export type RoutineRunResponse = z.infer<typeof RoutineRunResponseSchema>;
